import fetcher from 'api/fetcher';
import { AxiosError } from 'axios';
import { push } from 'connected-react-router';
import { BALANCE_REPLENISHMENT_TRIGGER_PARAM } from 'utils/balance/balanceReplenishment';
import { getNullableNumberFromString } from 'utils/clickme';
import { MagritteNotificationName } from 'utils/constants/notification';
import { FIRST_CAMPAIGN_AUCTION_PROMO } from 'utils/promoBonus';

import auctionCreateCampaignButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/auction_create_campaign_button_click';

import { selectAvailablePromos, setShouldSendUXFBEvent } from 'store/autoReducers';
import {
    AuctionBuilderErrors,
    AuctionCampaign,
    auctionSelector,
    auctionSelectorNonNullable,
    setErrors,
} from 'store/models/auctionData';
import { addMagritteNotification } from 'store/models/notification';
import { AppThunkAction } from 'store/utils';

export const startAuction = (setLoading: (loading: boolean) => void, dashboardRoute: string): AppThunkAction => {
    return (dispatch, getState) => {
        setLoading(true);

        const state = getState();
        const { value: bid } = auctionSelectorNonNullable.bid(state);
        const { value: budget } = auctionSelectorNonNullable.budget(state);
        const biddingStrategy = auctionSelectorNonNullable.biddingStrategy(state);
        const bidRating = auctionSelector.bidRating(state);
        const startAt = auctionSelector.startDate(state);
        const endAt = auctionSelector.endDate(state);
        const vacancyId = auctionSelector.vacancyId(state);

        const availablePromos = selectAvailablePromos(state);
        const isFirstCampaignAuctionPromoAvailable = availablePromos && availablePromos[FIRST_CAMPAIGN_AUCTION_PROMO];

        const body = {
            vacancyId: getNullableNumberFromString(vacancyId),
            bid,
            budget,
            startAt,
            endAt,
            biddingStrategy,
        };

        return fetcher
            .post<unknown, AuctionCampaign>('/api/v1/auction', body)
            .then(() => {
                auctionCreateCampaignButtonClick({
                    vacancyId: String(vacancyId),
                    bidInCents: bid || 0,
                    budgetInCents: budget || 0,
                    maxBidAmongSimilarVacancies: bidRating?.maxBidAmongSimilarVacancies || 0,
                    startAt,
                    endAt,
                    audiencePerWeek: bidRating?.audience?.audiencePerWeek,
                    audienceCapacityPerWeek: bidRating?.audience?.audienceCapacityPerWeek,
                    biddingStrategy,
                    ...(isFirstCampaignAuctionPromoAvailable && { promoKey: FIRST_CAMPAIGN_AUCTION_PROMO }),
                });

                dispatch(setShouldSendUXFBEvent(true));
                dispatch(push(`${dashboardRoute}?${BALANCE_REPLENISHMENT_TRIGGER_PARAM}`));
                dispatch(addMagritteNotification(MagritteNotificationName.AuctionBuilderCreateSuccess));
            })
            .catch((error: AxiosError<AuctionBuilderErrors>) => {
                setLoading(false);

                const response = error?.response;

                if (!response) {
                    return;
                }

                auctionCreateCampaignButtonClick({
                    vacancyId: String(vacancyId),
                    bidInCents: bid || 0,
                    budgetInCents: budget || 0,
                    errors: JSON.stringify(response.data),
                    audiencePerWeek: bidRating?.audience?.audiencePerWeek,
                    audienceCapacityPerWeek: bidRating?.audience?.audienceCapacityPerWeek,
                    biddingStrategy,
                    ...(isFirstCampaignAuctionPromoAvailable && { promoKey: FIRST_CAMPAIGN_AUCTION_PROMO }),
                });

                if (response.status === 400) {
                    dispatch([
                        addMagritteNotification(MagritteNotificationName.AuctionBuilderValidationError),
                        setErrors(response.data),
                    ]);
                    return;
                }

                if (response.status === 409) {
                    dispatch(addMagritteNotification(MagritteNotificationName.AuctionBuilderDuplicatedCampaignError));
                    return;
                }

                dispatch(addMagritteNotification(MagritteNotificationName.AuctionBuilderSomethingWentWrongError));
            });
    };
};
